import React from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { ACTION_META, API, MODE_LABELS, ROLE_NAMES } from './constants';

export const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{Object.entries(options).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const ROLE_OPTIONS = { admin: 'Admin', super_admin: 'Super Admin' };

const NamedApprovers = ({ companyId, value, onChange }) => {
  const { data } = useQuery({
    queryKey: ['v2-approvers', companyId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/approvers`)).data.approvers,
  });
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-slate-700" data-testid="rule-named-approvers">
      {(data || []).map((u) => (
        <label key={u.id} className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox checked={value.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`rule-approver-${u.id}`} />
          {u.full_name} <span className="text-xs text-slate-500">{u.email} · {ROLE_NAMES[u.role]}</span>
        </label>))}
    </div>
  );
};

const Levels = ({ levels, onChange }) => (
  <div className="space-y-2" data-testid="rule-levels">
    {levels.map((l, i) => (
      <div key={i} className="grid grid-cols-[60px_1fr_120px_40px] items-center gap-2">
        <span className="text-xs font-semibold text-slate-500">Nivel {i + 1}</span>
        <Pick value={l.role} onChange={(v) => onChange(levels.map((x, j) => (j === i ? { ...x, role: v } : x)))} options={ROLE_OPTIONS} testId={`rule-level-role-${i}`} />
        <Input type="number" min={1} value={l.min_approvals} onChange={(e) => onChange(levels.map((x, j) => (j === i ? { ...x, min_approvals: e.target.value } : x)))} data-testid={`rule-level-min-${i}`} />
        <Button type="button" variant="ghost" size="icon" onClick={() => onChange(levels.filter((_, j) => j !== i))} data-testid={`rule-level-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>))}
    <Button type="button" variant="outline" size="sm" onClick={() => onChange([...levels, { role: 'admin', min_approvals: 1 }])} data-testid="rule-add-level-btn"><Plus className="mr-1 h-4 w-4" />Nivel</Button>
  </div>
);

const ApprovalFields = ({ companyId, form, set }) => (
  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
    <div className="grid grid-cols-3 gap-2">
      <Pick value={form.approval_mode} onChange={(v) => set('approval_mode', v)} options={MODE_LABELS} testId="rule-mode-select" />
      {['single', 'quorum'].includes(form.approval_mode) && <Pick value={form.approver_role} onChange={(v) => set('approver_role', v)} options={{ admin: 'Aprueba: Admin', super_admin: 'Aprueba: Super Admin' }} testId="rule-approver-select" />}
      {form.approval_mode === 'quorum' && <Input type="number" min={1} value={form.min_approvals} onChange={(e) => set('min_approvals', e.target.value)} placeholder="Mínimo" data-testid="rule-min-approvals-input" />}
    </div>
    {form.approval_mode === 'all_named' && <NamedApprovers companyId={companyId} value={form.approver_user_ids} onChange={(v) => set('approver_user_ids', v)} />}
    {form.approval_mode === 'sequential' && <Levels levels={form.levels} onChange={(v) => set('levels', v)} />}
    <label className="flex items-center gap-2 text-xs text-slate-500">Expira en (horas)
      <Input type="number" min={1} className="w-24" value={form.expires_in_hours} onChange={(e) => set('expires_in_hours', e.target.value)} data-testid="rule-expires-input" /></label>
    <p className="text-xs text-slate-500">Un rechazo de cualquier aprobador habilitado rechaza la propuesta.</p>
  </div>
);

const ExceptionFields = ({ form, set }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50/50 p-3 text-sm dark:border-red-900 dark:bg-red-950/20">
    <label className="flex items-center gap-2"><Checkbox checked={form.allow_exception} onCheckedChange={(v) => set('allow_exception', !!v)} data-testid="rule-allow-exception" />Permitir excepción autorizada</label>
    {form.allow_exception && <div className="w-48"><Pick value={form.exception_role} onChange={(v) => set('exception_role', v)} options={{ admin: 'La autoriza: Admin', super_admin: 'La autoriza: Super Admin' }} testId="rule-exception-role" /></div>}
  </div>
);

export const RulePolicyFields = ({ companyId, form, set }) => (
  <div className="space-y-2">
    <div className="grid grid-cols-2 gap-2">
      <Pick value={form.action} onChange={(v) => set('action', v)} options={Object.fromEntries(Object.entries(ACTION_META).map(([k, m]) => [k, `Acción: ${m.label}`]))} testId="rule-action-select" />
      <Pick value={form.severity} onChange={(v) => set('severity', v)} options={{ low: 'Severidad baja', medium: 'Media', high: 'Alta', critical: 'Crítica' }} testId="rule-severity-select" />
    </div>
    <p className="text-xs text-slate-500" data-testid="rule-action-help">{ACTION_META[form.action].help}</p>
    {form.action === 'require_approval' && <ApprovalFields companyId={companyId} form={form} set={set} />}
    {form.action === 'block' && <ExceptionFields form={form} set={set} />}
  </div>
);
